import React, { useState, useEffect, createContext, useCallback, useContext } from 'react';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';

import { showFailure } from 'actions/notificationActions';

import { Card, Tabs, Modal } from 'antd';

import { AntdPageHeader, ContentWrapper, ApplicationUIContext } from '@yuhuinc/ui-library';

import {
  getIncentiveTemplates,
  getAssociations,
  createIncentiveTemplate,
  updateIncentiveTemplate,
  deleteIncentiveTemplate,
} from './store/actions';

import { Draft, Published, Archived, Statuses } from './Incentives/components/Form/constants';

import IncentiveTemplatesTable from './Incentives/components/IncentiveTemplatesTable';
import CreateIncentiveTemplate from './Incentives/CreateIncentiveTemplate';
import EditIncentiveTemplate from './Incentives/EditIncentiveTemplate';
import RowActions from './Incentives/components/Table/RowActions';
import { getTranslatedString } from 'helpers/i18n';
import { featureEnabled } from 'helpers/ToggleBot';
import { useMutation } from '@tanstack/react-query';
import { apiPost } from 'helpers/ApiHelper';
import { onError } from 'components/landlord_app/application_management/common/ErrorHandling';

export const IncentivesContext = createContext({});

const modes = {
  LIST: 'list',
  CREATE: 'create',
  EDIT: 'edit',
};

const goToCreateIncentivePage = () => browserHistory.push('/companies/incentives/new');
const goToIncentiveByIdPage = (incentiveId) =>
  browserHistory.push(`/companies/incentives/${incentiveId}`);

const LandlordIncentives = ({ showFailure, currentUser, router }) => {
  const [mode, setMode] = useState(modes.LIST);
  const [incentiveTemplates, setIncentiveTemplates] = useState([]);
  const [incentiveAssociations, setIncentiveAssociations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentIncentiveTemplate, setCurrentIncentiveTemplate] = useState({});
  const [currentIncentiveId, setCurrentIncentiveId] = useState(null);

  const { isMobile } = useContext(ApplicationUIContext);

  const loadAssociations = useCallback(() => {
    getAssociations()
      .done((response) => {
        setIncentiveAssociations(response.sites);
      })
      .catch((error) => {
        showFailure(error);
      });
  }, [showFailure]);

  const loadIncentiveTemplates = useCallback(() => {
    setLoading(true);
    getIncentiveTemplates()
      .done((response) => {
        setIncentiveTemplates(response.incentive_templates);
        setLoading(false);
      })
      .catch((error) => {
        console.error('request failed', error);
        showFailure(error);
      });
  }, [showFailure]);

  const duplicateIncentiveMutation = useMutation({
    mutationFn: (incentiveTemplateId) =>
      apiPost(`/landlords/incentives/incentive_templates/${incentiveTemplateId}/clone`),
    onSuccess: (response) => router.push(`/companies/incentives/${response.incentive_template.id}`),
    onError,
  });

  // Load the data needed to populate the context,
  // so that common data can be shared with descendant components
  // through the `useContext` hook.
  useEffect(() => {
    loadAssociations();
    loadIncentiveTemplates();
  }, [loadIncentiveTemplates, loadAssociations]);

  const getIncentiveTemplate = (incentiveTemplateId) => {
    return incentiveTemplates.find((f) => f.id === incentiveTemplateId);
  };

  const setModeToCreate = () => {
    if (featureEnabled('ui.incentives.overhaul')) {
      goToCreateIncentivePage();
    } else {
      setCurrentIncentiveTemplate({});
      setCurrentIncentiveId(null);
      setMode(modes.CREATE);
    }
  };

  const setModeToEdit = (incentiveTemplateId, incentiveId) => {
    if (featureEnabled('ui.incentives.overhaul')) {
      goToIncentiveByIdPage(incentiveTemplateId);
    } else {
      setCurrentIncentiveTemplate(getIncentiveTemplate(incentiveTemplateId));
      if (incentiveId) {
        setCurrentIncentiveId(incentiveId);
      }
      setMode(modes.EDIT);
    }
  };

  const setModeToList = () => {
    setCurrentIncentiveTemplate({});
    setCurrentIncentiveId(null);
    setMode(modes.LIST);
  };

  const handleCreateIncentiveTemplate = async (values) => {
    setLoading(true);
    await createIncentiveTemplate(values);

    loadIncentiveTemplates();
    setLoading(false);

    Modal.success({
      title: getTranslatedString('landlordPortal.incentives.successDraftTitle'),
      content: getTranslatedString('landlordPortal.incentives.successDraftDesc', {
        incentive_name: values.internal_name,
      }),
      okText: getTranslatedString('okay'),
      onOk: () => {
        setModeToList();
      },
    });
  };

  const handleUpdateIncentiveTemplate = async (values) => {
    setLoading(true);
    await updateIncentiveTemplate(values);

    loadIncentiveTemplates();
    setLoading(false);

    Modal.success({
      title: getTranslatedString('landlordPortal.incentives.successSavedTitle'),
      content: getTranslatedString('landlordPortal.incentives.successSavedDesc', {
        incentive_name: values.internal_name,
      }),
      okText: getTranslatedString('okay'),
      onOk: () => {
        setModeToList();
      },
    });
  };

  const handlePublishIncentiveTemplate = (values) => {
    const confirmationModal = Modal.confirm({
      title: getTranslatedString('landlordPortal.incentives.publishIncentiveTemplateTitle'),
      content: getTranslatedString('landlordPortal.incentives.publishIncentiveTemplateDesc'),
      onOk: async () => {
        confirmationModal.update({ okButtonProps: { loading: true } });

        if (values.id) {
          await updateIncentiveTemplate({ ...values, status: Published });
        } else {
          await createIncentiveTemplate({ ...values, status: Published });
        }

        confirmationModal.destroy();
        loadIncentiveTemplates();

        Modal.success({
          title: getTranslatedString('landlordPortal.incentives.successPublishedTitle'),
          content: getTranslatedString('landlordPortal.incentives.successPublishedDesc', {
            incentive_name: values.internal_name,
          }),
          okText: getTranslatedString('okay'),
          onOk: () => {
            setModeToList();
          },
        });
      },
      okText: getTranslatedString('landlordPortal.incentives.yesPublish'),
    });
  };

  const handleRepublishIncentiveTemplate = (values) => {
    const confirmationModal = Modal.confirm({
      title: getTranslatedString('landlordPortal.incentives.republishIncentiveTemplateTitle'),
      content: getTranslatedString('landlordPortal.incentives.republishIncentiveTemplateDesc'),
      onOk: async () => {
        confirmationModal.update({ okButtonProps: { loading: true } });

        if (values.id) {
          await updateIncentiveTemplate({ ...values, status: Published });
        } else {
          await createIncentiveTemplate({ ...values, status: Published });
        }

        confirmationModal.destroy();
        loadIncentiveTemplates();

        Modal.success({
          title: getTranslatedString('landlordPortal.incentives.successRepublishedTitle'),
          content: getTranslatedString('landlordPortal.incentives.successRepublishedDesc', {
            incentive_name: values.internal_name,
          }),
          okText: getTranslatedString('okay'),
          onOk: () => {
            setModeToList();
          },
        });
      },
      okText: getTranslatedString('landlordPortal.incentives.yesRepublish'),
    });
  };

  const handleDuplicateIncentiveTemplate = (incentiveTemplateId) => {
    if (featureEnabled('ui.incentives.overhaul')) {
      return duplicateIncentiveMutation.mutate(incentiveTemplateId);
    } else {
      const existingIncentiveTemplate = getIncentiveTemplate(incentiveTemplateId);
      const duplicateIncentiveTemplate = Object.assign({}, existingIncentiveTemplate);

      delete duplicateIncentiveTemplate.id;

      duplicateIncentiveTemplate.incentives.forEach((e) => {
        ['created_at', 'updated_at', 'id'].forEach((k) => delete e[k]);
      });
      duplicateIncentiveTemplate.internal_name = `Copy of ${duplicateIncentiveTemplate.internal_name}`;
      duplicateIncentiveTemplate.status = Draft;

      setCurrentIncentiveTemplate(duplicateIncentiveTemplate);
      setMode(modes.CREATE);
    }
  };

  const handleDeleteIncentiveTemplate = (incentiveTemplateId) => {
    const confirmationModal = Modal.confirm({
      title: getTranslatedString('landlordPortal.incentives.deleteIncentiveTemplateTitle'),
      content: getTranslatedString('landlordPortal.incentives.deleteIncentiveTemplateDesc'),
      okText: getTranslatedString('landlordPortal.incentives.yesDelete'),
      onOk: async () => {
        confirmationModal.update({ okButtonProps: { loading: true } });

        await deleteIncentiveTemplate(incentiveTemplateId);

        const incentive = { ...getIncentiveTemplate(incentiveTemplateId) };

        confirmationModal.destroy();
        loadIncentiveTemplates();

        Modal.success({
          title: getTranslatedString('landlordPortal.incentives.successDeletedTitle'),
          content: getTranslatedString('landlordPortal.incentives.successDeletedDesc', {
            incentive_name: incentive.internal_name,
          }),
          okText: getTranslatedString('okay'),
          onOk: () => {
            setModeToList();
          },
        });
      },
    });
  };

  const handleArchiveIncentiveTemplate = (values) => {
    const confirmationModal = Modal.confirm({
      title: getTranslatedString('landlordPortal.incentives.archiveIncentiveTemplateTitle'),
      content: getTranslatedString('landlordPortal.incentives.archiveIncentiveTemplateDesc'),
      okText: getTranslatedString('landlordPortal.incentives.yesArchive'),
      onOk: async () => {
        confirmationModal.update({ okButtonProps: { loading: true } });

        await updateIncentiveTemplate({ ...values, status: Archived });

        const incentive = { ...values, ...getIncentiveTemplate(values.id) };

        confirmationModal.destroy();
        loadIncentiveTemplates();

        Modal.success({
          title: getTranslatedString('landlordPortal.incentives.successArchivedTitle'),
          content: getTranslatedString('landlordPortal.incentives.successArchivedDesc', {
            incentive_name: incentive.internal_name,
          }),
          okText: getTranslatedString('okay'),
          onOk: () => {
            setModeToList();
          },
        });
      },
    });
  };

  let pageContent = null;

  if (mode === modes.LIST) {
    pageContent = (
      <>
        <AntdPageHeader
          title={getTranslatedString('landlordPortal.incentives.incentives')}
          style={{ paddingBottom: 0 }}
        />

        <ContentWrapper>
          <Tabs defaultActiveKey="published_and_draft">
            <Tabs.TabPane
              tab={getTranslatedString('landlordPortal.incentives.publishedAndDraft')}
              key="published_and_draft"
            >
              <Card>
                <IncentiveTemplatesTable
                  showActionButton={currentUser.admin}
                  actionButtonLabel={getTranslatedString(
                    'landlordPortal.incentives.addIncentiveTemplate'
                  )}
                  actionButtonOnClick={setModeToCreate}
                  onEditClick={setModeToEdit}
                  statusFilters={[Draft, Published].map((s) => ({ value: s, text: Statuses[s] }))}
                  renderRowActions={(rowData) => (
                    <RowActions
                      rowData={rowData}
                      onEdit={currentUser.admin && setModeToEdit}
                      onDuplicate={
                        currentUser.admin
                          ? (incentiveTemplateId) =>
                              handleDuplicateIncentiveTemplate(incentiveTemplateId)
                          : null
                      }
                      onArchive={
                        currentUser.admin
                          ? (incentiveTemplateId) =>
                              handleArchiveIncentiveTemplate(
                                getIncentiveTemplate(incentiveTemplateId)
                              )
                          : null
                      }
                    />
                  )}
                  onResetClick={() => loadIncentiveTemplates()}
                  incentiveTemplateIncluded={(f) => [Published, Draft].includes(f.status)}
                />
              </Card>
            </Tabs.TabPane>
            <Tabs.TabPane
              tab={getTranslatedString('landlordPortal.incentives.archived')}
              key="archived"
            >
              <Card bodyStyle={{ padding: isMobile && 16, fontSize: isMobile && 16 }}>
                <IncentiveTemplatesTable
                  showActionButton={currentUser.admin}
                  actionButtonLabel={getTranslatedString(
                    'landlordPortal.incentives.addIncentiveTemplate'
                  )}
                  actionButtonOnClick={setModeToCreate}
                  onEditClick={setModeToEdit}
                  renderRowActions={(rowData) => (
                    <RowActions
                      rowData={rowData}
                      onEdit={currentUser.admin && setModeToEdit}
                      onDuplicate={
                        currentUser.admin
                          ? (incentiveTemplateId) =>
                              handleDuplicateIncentiveTemplate(incentiveTemplateId)
                          : null
                      }
                      onDelete={
                        currentUser.admin
                          ? (incentiveTemplateId) =>
                              handleDeleteIncentiveTemplate(incentiveTemplateId)
                          : null
                      }
                    />
                  )}
                  onResetClick={() => loadIncentiveTemplates()}
                  incentiveTemplateIncluded={(f) => [Archived].includes(f.status)}
                />
              </Card>
            </Tabs.TabPane>
          </Tabs>
        </ContentWrapper>
      </>
    );
  } else if (mode === modes.CREATE) {
    pageContent = (
      <CreateIncentiveTemplate
        onBack={setModeToList}
        onCreate={currentUser.admin && handleCreateIncentiveTemplate}
        onPublish={currentUser.admin && handlePublishIncentiveTemplate}
      />
    );
  } else if (mode === modes.EDIT) {
    pageContent = (
      <EditIncentiveTemplate
        onBack={setModeToList}
        onArchive={currentUser.admin && handleArchiveIncentiveTemplate}
        onPublish={currentUser.admin && handlePublishIncentiveTemplate}
        onRepublish={currentUser.admin && handleRepublishIncentiveTemplate}
        onDelete={currentUser.admin && handleDeleteIncentiveTemplate}
        onUpdate={currentUser.admin && handleUpdateIncentiveTemplate}
      />
    );
  }

  const contextData = {
    isMobile: isMobile,
    incentiveTemplates: incentiveTemplates,
    incentiveAssociations: incentiveAssociations,
    mode: mode,
    loading: loading,
    currentIncentiveTemplate: currentIncentiveTemplate,
    currentIncentiveId: currentIncentiveId,
  };

  // Write the contextData into the `Provider`,
  // making it available to descendant components
  return (
    <div className="body" role="main">
      <IncentivesContext.Provider value={contextData}>{pageContent}</IncentivesContext.Provider>
    </div>
  );
};

export default connect((store) => ({ currentUser: store.userTypes }), { showFailure })(
  LandlordIncentives
);
